import { useApiClient } from '@siclop/kstone-vue';
import { apiClient } from './client';
import type {
  AvailableBuildingView,
  AvailableShipView,
  BuildBuildingActionPDTO,
  BuildingStatus,
  PlanetBuildingModel,
  PlanetCoefficientsView,
  PlanetDetailView,
  PlanetPublicView,
  PlanetResourceModel,
  ToggleBuildingActionPDTO,
  UpgradeBuildingActionPDTO,
} from '@/types/planet';
import type { PlanetDashboardDto } from '@/types/planetDashboard';

const BASE = '/api/v1/Planet';

/**
 * Lot 35A/B — Recupere tout ce dont la planet view a besoin en 1 call :
 * detail + race ideals + ressources + coefficients + catalogue de
 * batiments groupe par categorie.
 */
export async function fetchPlanetDashboard(planetId: number): Promise<PlanetDashboardDto> {
  const { data } = await apiClient.get<PlanetDashboardDto>(`${BASE}/${planetId}/dashboard`);
  return data;
}

// ---------------------------------------------------------------------------
// Vues
// ---------------------------------------------------------------------------
export async function fetchPlanetDetail(planetId: number): Promise<PlanetDetailView> {
  const { data } = await apiClient.get<PlanetDetailView>(`${BASE}/${planetId}/detail`);
  return data;
}

export async function fetchPlanetPublic(planetId: number): Promise<PlanetPublicView> {
  const { data } = await apiClient.get<PlanetPublicView>(`${BASE}/${planetId}/public`);
  return data;
}

export async function fetchPlanetBuildings(planetId: number): Promise<PlanetBuildingModel[]> {
  const { data } = await apiClient.get<PlanetBuildingModel[]>(`${BASE}/${planetId}/buildings`);
  return data;
}

export async function fetchAvailableBuildings(
  planetId: number,
  kind?: string,
): Promise<AvailableBuildingView[]> {
  const { data } = await apiClient.get<AvailableBuildingView[]>(
    `${BASE}/${planetId}/buildings/available`,
    { params: kind ? { kind } : undefined },
  );
  return data;
}

/**
 * Lot 12.1.b — Designs de vaisseaux du proprietaire de la planete annotes
 * avec leurs prereqs (recherche + niveau d'usine) sur cette planete.
 */
export async function fetchAvailableShips(planetId: number): Promise<AvailableShipView[]> {
  const { data } = await apiClient.get<AvailableShipView[]>(
    `${BASE}/${planetId}/ships/available`,
  );
  return data;
}

export async function fetchPlanetResources(planetId: number): Promise<PlanetResourceModel[]> {
  const { data } = await apiClient.get<PlanetResourceModel[]>(`${BASE}/${planetId}/resources`);
  return data;
}

export async function fetchPlanetCoefficients(planetId: number): Promise<PlanetCoefficientsView> {
  const { data } = await apiClient.get<PlanetCoefficientsView>(`${BASE}/${planetId}/coefficients`);
  return data;
}

// ---------------------------------------------------------------------------
// Liste paginee (CRUD KStone -- enveloppe { total, items })
// ---------------------------------------------------------------------------
export interface PlanetListItem {
  id: number;
  number: number;
  coordinates?: string | null;
  imageCode?: string | null;
  ownerPlayerId?: number | null;
  planetTypeId?: number | null;
  solarSystemId?: number | null;
  places: number;
}

/**
 * Payload paginé historique : `{ items, total }`. La grande majorité des
 * endpoints du projet renvoie ce shape (via wrapper custom ou attendu
 * implicitement par les vues). Conserve l'API stricte (items et total
 * requis) pour ne pas propager `undefined` dans les vues consommatrices.
 */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
}

/**
 * Shape brute possible retournée par certains endpoints KStone :
 * - `KController.Get(filter)` sans paging peut renvoyer un array T[] ;
 * - `KController.GetPaged(filter)` renvoie `{ data, totalCount }`
 *   (KStone PagedResult, serialise en camelCase).
 *
 * `normalizePaginated` absorbe ces 3 shapes (array, KStone, historique)
 * pour renvoyer toujours `{ items, total }`.
 */
export function normalizePaginated<T>(
  resp: PaginatedResult<T> | { data?: T[]; totalCount?: number } | T[] | null | undefined,
): PaginatedResult<T> {
  if (resp == null) return { items: [], total: 0 };
  if (Array.isArray(resp)) return { items: resp, total: resp.length };
  const r = resp as { items?: T[]; total?: number; data?: T[]; totalCount?: number };
  const items = r.items ?? r.data ?? [];
  const total = r.total ?? r.totalCount ?? items.length;
  return { items, total };
}

/**
 * Liste des planètes possédées par un joueur (Player.Id, pas User.Id !).
 * Récupère Player.Id via fetchMe() depuis @/api/me.
 */
export async function fetchMyPlanets(playerId: number): Promise<PaginatedResult<PlanetListItem>> {
  const { data } = await apiClient.get<PaginatedResult<PlanetListItem>>(`${BASE}`, {
    params: { ownerPlayerId: playerId, pageSize: 200 },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Actions metier sur les batiments
// ---------------------------------------------------------------------------
export async function startBuild(
  planetId: number,
  body: BuildBuildingActionPDTO,
): Promise<unknown> {
  const { data } = await apiClient.post(`${BASE}/${planetId}/buildings`, body);
  return data;
}

export async function startUpgrade(
  planetId: number,
  planetBuildingId: number,
  body: UpgradeBuildingActionPDTO,
): Promise<unknown> {
  const { data } = await apiClient.post(
    `${BASE}/${planetId}/buildings/${planetBuildingId}/upgrade`,
    body,
  );
  return data;
}

export async function recycleBuilding(planetId: number, planetBuildingId: number): Promise<void> {
  await apiClient.post(`${BASE}/${planetId}/buildings/${planetBuildingId}/recycle`);
}

/** Abandonne une colonie (non capitale) — irréversible. */
export async function abandonColony(planetId: number): Promise<void> {
  await apiClient.post(`${BASE}/${planetId}/abandon-colony`);
}

/** Abandonne la PARTIE (capitale) : décolonise tout + archive le PU. Irréversible. */
export async function abandonGame(capitalPlanetId: number): Promise<void> {
  await apiClient.post(`${BASE}/${capitalPlanetId}/abandon-game`);
}

// ---------------------------------------------------------------------------
// Lot 38 — Production de vaisseaux (chantier orbital / usines)
// ---------------------------------------------------------------------------

export interface ProduceShipActionPDTO {
  playerUniverseId: number;
  shipId: number;
  quantity: number;
}

/** Aligne sur ShipProductionTaskModel (heritage ATask) */
export type ShipProductionTaskStatus = 'Pending' | 'Running' | 'Completed' | 'Cancelled' | 'Failed';

export interface ShipProductionTaskModel {
  id: number;
  planetId: number;
  shipId: number;
  totalQuantity: number;
  remainingQuantity: number;
  unitSeconds: number;
  startedAt: string;
  finishAt: string;
  /** Sérialisé en string par le backend (StringEnumConverter). */
  taskStatus: ShipProductionTaskStatus;
  playerUniverseId?: number;
  /** True = en file d'attente (chaine séquentielle) ; false = tête de chaine en construction. */
  isWaiting: boolean;
}

export async function produceShips(planetId: number, body: ProduceShipActionPDTO): Promise<unknown> {
  const { data } = await apiClient.post(`${BASE}/${planetId}/ships/produce`, body);
  return data;
}

export async function fetchPlanetShipProductionTasks(planetId: number): Promise<ShipProductionTaskModel[]> {
  // Endpoint KController racine ([HttpGet] -> _services.List(filter)) : renvoie un
  // TABLEAU brut, PAS un PagedResult (celui-ci est sur /paged). On passe par le
  // client KStone useApiClient().getAll, qui fait la liaison correcte avec l'API
  // KStone (auth + baseURL partagés). Auparavant on lisait `data.data` en dur ->
  // `undefined` -> `t.filter` levait une TypeError attrapée -> toast « Erreur
  // inconnue » + chantier sans aucune info.
  return useApiClient().getAll<ShipProductionTaskModel>(
    '/api/v1/ShipProductionTask',
    { params: { planetId, pageSize: 100 } },
  );
}

/** Lot 9D — Annule une tache de construction/upgrade et rembourse les ressources. */
export async function cancelBuildTask(planetId: number, taskId: number): Promise<void> {
  await apiClient.post(`${BASE}/${planetId}/build-tasks/${taskId}/cancel`);
}

/** Lot 9D — Annule une tache de production de vaisseaux et rembourse le flouz. */
export async function cancelShipProductionTask(planetId: number, taskId: number): Promise<void> {
  await apiClient.post(`${BASE}/${planetId}/ship-production-tasks/${taskId}/cancel`);
}

export async function toggleBuilding(
  planetId: number,
  planetBuildingId: number,
  body?: ToggleBuildingActionPDTO,
): Promise<{ unitStatus: BuildingStatus }> {
  const { data } = await apiClient.post<{ unitStatus: BuildingStatus }>(
    `${BASE}/${planetId}/buildings/${planetBuildingId}/toggle`,
    body ?? {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Couts ressources des vaisseaux (gemmes + flouz)
// ---------------------------------------------------------------------------
export interface ShipTypeResourceCost {
  resourceId: number;
  code: string;
  label: string;
  baseAmount: number;
}

export interface ShipTypeCostDto {
  shipTypeId: number;
  code: string;
  label: string;
  /** Cout en flouz par unite produite. */
  baseFlouz: number;
  /** Couts en ressources (gemmes) par unite. */
  resources: ShipTypeResourceCost[];
}

/**
 * Recupere le cout par unite pour un ShipType : flouz + ressources
 * (gemmes). Le shipyard l'utilise pour afficher les couts + verifier
 * la disponibilite avant de lancer une production (debit cote serveur).
 */
export async function fetchShipTypeCost(shipTypeId: number): Promise<ShipTypeCostDto> {
  const { data } = await apiClient.get<ShipTypeCostDto>(
    `/api/v1/ShipType/${shipTypeId}/resources`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Taxation planetaire (flouz)
// ---------------------------------------------------------------------------
/**
 * Change le taux de taxation de la planete. Le backend recalcule
 * immediatement la production de flouz (ProductionPerHour) qui sera
 * creditee au prochain tick par PlanetProductionService.
 */
export async function setPlanetTaxRate(planetId: number, taxRate: number): Promise<{ taxRate: number }> {
  const { data } = await apiClient.post<{ taxRate: number }>(
    `${BASE}/${planetId}/tax-rate`,
    { taxRate },
  );
  return data;
}

/**
 * Renomme une planete (owner uniquement). Le serveur valide la longueur
 * (1..60 chars trim) et renvoie le nom canonise.
 */
export async function renamePlanet(planetId: number, name: string): Promise<{ name: string }> {
  const { data } = await apiClient.post<{ name: string }>(
    `${BASE}/${planetId}/rename`,
    { name },
  );
  return data;
}

/** Sélectionne (ou arrête : 'None') l'axe d'environnement terraformé. */
export async function setTerraformAxis(planetId: number, axis: string): Promise<{ axis: string }> {
  const { data } = await apiClient.post<{ axis: string }>(
    `${BASE}/${planetId}/terraform`,
    { axis },
  );
  return data;
}
